import type { GQL_ID, NestedFilter } from '@@partner:graphql/types'
import type { RsuiteItemDataType } from '@@partner:types:app'

import React, { useState, useCallback, useMemo } from 'react'
import { CheckPicker, Form, Panel } from 'rsuite'
import { useDebounce } from 'use-debounce'

import { useFetchQuery as useFetchBrands } from '@@partner:graphql/query/catalog/productAvailableBrands.gql'
import CategoriesFilter from '@components/catalog/product/grid/CategoriesFilter'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'
import { MainPageBlockForm } from '../form/MainPageConfigFormState'

interface ProductsFilterBlockProps {
  block: MainPageBlockForm
  onChange: (block: MainPageBlockForm) => void
}

export interface ProductFilterTranslation {
  language_code: string
  label: string
}

export interface ProductFilterPayload {
  label?: string
  languages?: string[]
  i18n?: ProductFilterTranslation[]
  filter: NestedFilter
  brandIds?: GQL_ID[]
  categoryIds?: GQL_ID[]
  manufacturer?: string
  country?: string
}

// Компонент для настройки фильтра товаров на главной странице
// Позволяет выбрать: Бренд товара (ProductBrand), Категории товаров, Производитель, Страна производства

export default function ProductsFilterBlock ({
  block,
  onChange
}: ProductsFilterBlockProps) {
  const payload = useMemo<ProductFilterPayload>(() => (block.payload as ProductFilterPayload) || {
    label: '',
    languages: [],
    i18n: [],
    filter: { op: 'unwrap', fields: [], value: [] },
    brandIds: [],
    categoryIds: [],
    manufacturer: '',
    country: ''
  }, [ block.payload ])

  // Состояния для поиска
  const [ selectedLanguage, setSelectedLanguage ] = useState<string | undefined>()
  const [ labelInput, setLabelInput ] = useState(payload.label || '')
  const [ brandSearchName, setBrandSearchName ] = useState('')
  const [ manufacturerInput, setManufacturerInput ] = useState(payload.manufacturer || '')
  const [ countryInput, setCountryInput ] = useState(payload.country || '')
  const [ debouncedBrandSearchName ] = useDebounce(brandSearchName, 500)

  // Загрузка брендов
  const { data: brandsData, loading: brandsLoading } = useFetchBrands({ 
    name: debouncedBrandSearchName 
  })

  const brandsOptions: RsuiteItemDataType[] = useMemo(() => 
    brandsData?.brands.map(brand => ({
      label: brand.name,
      value: brand.id
    })) ?? [], 
  [ brandsData ])

  // Построение NestedFilter из выбранных значений
  const buildNestedFilter = useCallback((
    brandIds: GQL_ID[],
    categoryIds: GQL_ID[],
    manufacturer: string,
    country: string
  ): NestedFilter => {
    const andConditions: NestedFilter[] = []

    if (brandIds.length > 0) {
      andConditions.push({
        op: 'contains',
        fields: [ 'brand_id' ],
        value: brandIds as (string | number)[]
      })
    }

    if (categoryIds.length > 0) {
      andConditions.push({
        op: 'contains',
        fields: [ 'category_id' ],
        value: categoryIds as (string | number)[]
      })
    }

    if (manufacturer.trim().length > 0) {
      andConditions.push({
        op: 'includes',
        fields: [ 'manufacturer' ],
        value: [ manufacturer.trim() ]
      })
    }

    if (country.trim().length > 0) {
      andConditions.push({
        op: 'includes',
        fields: [ 'country' ],
        value: [ country.trim() ]
      })
    }

    if (andConditions.length === 0) {
      return { op: 'unwrap', fields: [], value: [] }
    }

    return {
      and: andConditions
    }
  }, [])

  // Обработчики изменений
  const handleBrandChange = useCallback((brandIds: GQL_ID[]) => {
    const newPayload: ProductFilterPayload = {
      ...payload,
      brandIds,
      filter: buildNestedFilter(brandIds, payload.categoryIds || [], payload.manufacturer || '', payload.country || '')
    }
    onChange({
      ...block,
      payload: newPayload
    })
  }, [ block, onChange, payload, buildNestedFilter ])

  const handleCategoryChange = useCallback((categoryIds: GQL_ID[]) => {
    const newPayload: ProductFilterPayload = {
      ...payload,
      categoryIds,
      filter: buildNestedFilter(payload.brandIds || [], categoryIds, payload.manufacturer || '', payload.country || '')
    }
    onChange({
      ...block,
      payload: newPayload
    })
  }, [ block, onChange, payload, buildNestedFilter ])

  const handleManufacturerChange = useCallback((value: string) => {
    setManufacturerInput(value)
  }, [])

  const handleManufacturerBlur = useCallback(() => {
    const newPayload: ProductFilterPayload = {
      ...payload,
      manufacturer: manufacturerInput,
      filter: buildNestedFilter(payload.brandIds || [], payload.categoryIds || [], manufacturerInput, payload.country || '')
    }
    onChange({
      ...block,
      payload: newPayload
    })
  }, [ block, onChange, payload, manufacturerInput, buildNestedFilter ])

  const handleCountryChange = useCallback((value: string) => {
    setCountryInput(value)
  }, [])

  const handleCountryBlur = useCallback(() => {
    const newPayload: ProductFilterPayload = {
      ...payload,
      country: countryInput,
      filter: buildNestedFilter(payload.brandIds || [], payload.categoryIds || [], payload.manufacturer || '', countryInput)
    }
    onChange({
      ...block,
      payload: newPayload
    })
  }, [ block, onChange, payload, countryInput, buildNestedFilter ])

  const handleLabelChange = useCallback((value: string) => {
    setLabelInput(value)
  }, [])

  const handleLabelBlur = useCallback(() => {
    const newPayload: ProductFilterPayload = {
      ...payload,
      label: labelInput
    }
    onChange({
      ...block,
      payload: newPayload
    })
  }, [ block, onChange, payload, labelInput ])

  const handleAddLanguage = useCallback((language: string) => {
    const newLanguages = [ ...(payload.languages || []), language ]
    const newPayload: ProductFilterPayload = {
      ...payload,
      languages: newLanguages
    }
    onChange({
      ...block,
      payload: newPayload
    })
  }, [ block, onChange, payload ])

  const handleTranslateChange = useCallback((value: string) => {
    if (!selectedLanguage) return

    const newTranslations = [ ...(payload.i18n || []) ]
    const translationIndex = newTranslations.findIndex(
      translation => translation.language_code === selectedLanguage
    )

    if (translationIndex >= 0) {
      newTranslations[translationIndex] = {
        ...newTranslations[translationIndex],
        label: value
      }
    }
    else {
      newTranslations.push({
        language_code: selectedLanguage,
        label: value
      })
    }

    const newPayload: ProductFilterPayload = {
      ...payload,
      i18n: newTranslations
    }
    onChange({
      ...block,
      payload: newPayload
    })
  }, [ block, onChange, payload, selectedLanguage ])

  return (
    <Panel bordered header="Настройка фильтра товаров">
      <Form fluid>
        <Form.Group>
          <Form.ControlLabel>Заголовок блока</Form.ControlLabel>
          <Form.Control
            name="label"
            placeholder="Введите заголовок блока"
            value={labelInput}
            onChange={handleLabelChange}
            onBlur={handleLabelBlur}
          />
          <Form.HelpText>
            Заголовок, который будет отображаться над списком товаров
          </Form.HelpText>
        </Form.Group>

        <Form.Group>
          <Form.ControlLabel>Переводы заголовка</Form.ControlLabel>
          <SelectLocale
            selectedLanguage={selectedLanguage}
            onSelect={(language) => setSelectedLanguage(language)}
            onAdd={handleAddLanguage}
            languages={payload.languages || []}
          />
          {selectedLanguage && (
            <LocalizedForm
              name="label"
              label="Заголовок"
              selectedLanguage={selectedLanguage}
              value={payload.i18n?.find(t => t.language_code === selectedLanguage)?.label || ''}
              onChange={handleTranslateChange}
            />
          )}
        </Form.Group>

        <Form.Group>
          <Form.ControlLabel>Бренд товара</Form.ControlLabel>
          <CheckPicker
            block
            searchable
            data={brandsOptions}
            value={payload.brandIds || []}
            loading={brandsLoading}
            onSelect={handleBrandChange}
            onSearch={setBrandSearchName}
            placeholder="Выберите бренды"
          />
        </Form.Group>

        <Form.Group>
          <Form.ControlLabel>Категории</Form.ControlLabel>
          <CategoriesFilter
            value={payload.categoryIds || []}
            multiple={true}
            onChange={handleCategoryChange}
          />
        </Form.Group>

        <Form.Group>
          <Form.ControlLabel>Производитель</Form.ControlLabel>
          <Form.Control
            name="manufacturer"
            placeholder="Введите название производителя"
            value={manufacturerInput}
            onChange={handleManufacturerChange}
            onBlur={handleManufacturerBlur}
          />
          <Form.HelpText>
            Поиск по полю производителя товара (частичное совпадение)
          </Form.HelpText>
        </Form.Group>

        <Form.Group>
          <Form.ControlLabel>Страна производства</Form.ControlLabel>
          <Form.Control
            name="country"
            placeholder="Введите страну производства"
            value={countryInput}
            onChange={handleCountryChange}
            onBlur={handleCountryBlur}
          />
          <Form.HelpText>
            Поиск по полю страны производства товара (частичное совпадение)
          </Form.HelpText>
        </Form.Group>
      </Form>

      {/* Debug: отображение текущего фильтра */}
      {process.env.NODE_ENV === 'development' && (
        <details style={{ marginTop: 16 }}>
          <summary>Структура фильтра (NestedFilter)</summary>
          <pre style={{ fontSize: 12, background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
            {JSON.stringify(payload.filter, null, 2)}
          </pre>
        </details>
      )}
    </Panel>
  )
}
